import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  MapPin,
  Save,
  Check,
  Loader2,
  ChevronDown,
  AlertCircle,
  X,
  ArrowLeft,
  Shield,
  Users
} from "lucide-react";
import { regionOptions, locationsByRegion } from "../../../utils/data";
import Navbar from "../../../components/Navbar";
import Config from "../../../Config";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import EmptyState from "../../../components/Partners/EmptyState";
import BranchPreview from "../../../components/Partners/BranchPreview";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";
import { PERMISSIONS } from "../../../config/permissions";
import Loader from "../../../components/Loader";

const OptionalLabel = () => (
  <span className="ml-1 text-xs text-gray-400">(optional)</span>
);

const PartnerEdit = () => {
  const { partner_slug } = useParams();
  const navigate = useNavigate();
  
  // Permission checks
  const { 
    canPerform, 
    isAuthenticated,
    isMainVendor 
  } = usePermissionCheck();

  // Check if user can edit partners
  const canEditPartners = canPerform(PERMISSIONS.MODULES.PARTNER_MANAGEMENT, PERMISSIONS.ACTIONS.EDIT_PARTNER) || isMainVendor;

  // Debug logging for permission checks
  console.log('PartnerEdit Permission Debug:', {
    isAuthenticated,
    isMainVendor,
    canEditPartners,
    partner_slug
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [originalBranches, setOriginalBranches] = useState([]);

  const [partnerData, setPartnerData] = useState({
    name: "",
    branches: [],
  });

  const [currentBranch, setCurrentBranch] = useState({
    branch_slug: "",
    name: "",
    region: "",
    location: "",
    customLocation: "",
    district: "",
    suburb: "",
    landmark: "",
    gpsAddress: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState({
    partnerName: "",
    branchName: "",
    phoneNumber: "",
    region: "",
    location: "",
    customLocation: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isCustomLocation, setIsCustomLocation] = useState(false);

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Fetch partner details
  useEffect(() => {
    const fetchPartnerDetails = async () => {
      if (!canEditPartners) return; // Don't fetch if no permission
      
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${Config.backendDomain}vendor/partner/${partner_slug}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("dealboxx_token")}`,
            },
          }
        );

        const { data } = response;
        console.log("Partner data:", data?.data?.data[0]);
        if (data?.data?.status_code === "000" && !data?.data?.in_error) {
          const partner = data?.data?.data[0];
          setPartnerData({
            name: partner.name,
            branches: partner.branches.map(branch => ({
              ...branch,
              phoneNumber: branch.phone_number,
              branch_slug: branch.branch_slug || ""
            }))
          });
          setOriginalBranches(partner.branches.map(b => b.branch_slug));
        } else {
          throw new Error(data?.data?.reason || "Failed to fetch partner details");
        }
      } catch (error) {
        console.error("Error fetching partner:", error);
        toast.error(error?.response?.data?.reason || error.message || "Failed to fetch partner details");
        navigate("/partners");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPartnerDetails();
  }, [partner_slug, navigate, canEditPartners]);

  useEffect(() => {
    if (currentBranch.region) {
      const locations = locationsByRegion[currentBranch.region] || [];
      setAvailableLocations([...locations, "Other"]);
    }
  }, [currentBranch.region]);

  // NOW WE CAN HAVE CONDITIONAL RETURNS AFTER ALL HOOKS
  // Access denied screen for users without edit permissions
  if (!isAuthenticated || !canEditPartners) {
    return (
      <div className="bg-white">
        <Navbar page="Edit Partner" />
        <div className="flex justify-center items-center min-h-[80vh] bg-gray-50">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">Access Restricted</h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to edit partners. Please contact your administrator to request access to the Partner Editing feature.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Required Permission:</span>
              </div>
              <p className="text-sm text-blue-700">Edit Partner in Partner Management</p>
            </div>
            <div className="flex gap-3 mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/partners')}
                className="px-4 py-2 font-medium text-white bg-gray-500 rounded-lg transition-colors hover:bg-gray-600"
              >
                View Partners
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-[#FF6C6C] text-white rounded-lg hover:bg-[#FF6C6C]/90 transition-colors font-medium"
              >
                Return to Dashboard
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return <Loader />;
  }

  const validatePartnerName = (name) => {
    if (!name.trim()) return "Partner name is required";
    if (name.length < 3) return "Partner name must be at least 3 characters";
    if (name.length > 50) return "Partner name must be less than 50 characters";
    return "";
  };

  const validateBranchName = (name) => {
    if (!name.trim()) return "Branch name is required";
    if (name.length < 3) return "Branch name must be at least 3 characters";
    if (name.length > 50) return "Branch name must be less than 50 characters";
    return "";
  };

  const validatePhoneNumber = (number) => {
    if (!number) return "Phone number is required";
    if (number.length !== 9) return "Phone number must be exactly 9 digits";
    if (!/^\d+$/.test(number)) return "Phone number must contain only digits";
    return "";
  };

  const validateLocation = () => {
    if (!currentBranch.region) return "Region is required";
    if (!currentBranch.location && !isCustomLocation) return "Location is required";
    if (isCustomLocation && !currentBranch.customLocation.trim()) return "Custom location is required";
    return "";
  };

  const handleRegionChange = (region) => {
    setCurrentBranch((prev) => ({
      ...prev,
      region,
      location: "",
      customLocation: "",
    }));
    setIsCustomLocation(false);
    setErrors((prev) => ({ ...prev, region: "", location: "" }));
  };

  const handleLocationChange = (location) => {
    setCurrentBranch((prev) => ({
      ...prev,
      location,
      customLocation: "",
    }));
    setIsCustomLocation(location === "Other");
    setErrors((prev) => ({ ...prev, location: "", customLocation: "" }));
  };

  const validateBranchForm = () => {
    const newErrors = {
      branchName: validateBranchName(currentBranch.name),
      phoneNumber: validatePhoneNumber(currentBranch.phoneNumber),
      location: validateLocation(),
    };

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return !Object.values(newErrors).some(Boolean);
  };

  const applyBranch = () => {
    if (!validateBranchForm()) return;

    const branchToAdd = {
      ...currentBranch,
      location: isCustomLocation ? currentBranch.customLocation : currentBranch.location,
      phoneNumber: `233${currentBranch.phoneNumber}`,
    };

    setPartnerData((prev) => ({
      ...prev,
      branches: isEditing
        ? prev.branches.map((branch) =>
            branch.branch_slug === branchToAdd.branch_slug ? branchToAdd : branch
          )
        : [...prev.branches, branchToAdd],
    }));

    // Reset form
    setCurrentBranch({
      branch_slug: "",
      name: "",
      region: "",
      location: "",
      customLocation: "",
      district: "",
      suburb: "",
      landmark: "",
      gpsAddress: "",
      phoneNumber: "",
    });
    setIsEditing(false);
    setIsCustomLocation(false);
    setErrors({});

    toast.success(isEditing ? "Branch updated successfully" : "Branch added successfully", {
      position: "bottom-right",
      style: { background: "#10B981", color: "white" }
    });
  };

  const removeBranch = (branchSlug) => {
    if (partnerData.branches.length <= 1) {
      toast.warning("Partner must have at least one branch", {
        position: "top-right",
        style: { background: "#F59E0B", color: "white" }
      });
      return;
    }
    
    setPartnerData((prev) => ({
      ...prev,
      branches: prev.branches.filter((branch) => branch.branch_slug !== branchSlug),
    }));
    toast.info("Branch removed", {
      position: "bottom-right",
      style: { background: "#6B7280", color: "white" }
    });
  };

  const handleEditBranch = (branch) => {
    const isLocationCustom = !locationsByRegion[branch.region]?.includes(
      branch.location
    );

    setCurrentBranch({
      ...branch,
      phoneNumber: branch.phoneNumber.replace("233", ""),
      customLocation: isLocationCustom ? branch.location : "",
      location: isLocationCustom ? "Other" : branch.location,
    });
    setIsEditing(true);
    setIsCustomLocation(isLocationCustom);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate partner name
    const partnerNameError = validatePartnerName(partnerData.name);
    if (partnerNameError) {
      setErrors((prev) => ({ ...prev, partnerName: partnerNameError }));
      toast.error("Please enter a valid partner name");
      return;
    }

    // Validate branches
    if (partnerData.branches.length === 0) {
      toast.error("Please add at least one branch");
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        name: partnerData.name,
        branches: partnerData.branches.map(({ customLocation, phoneNumber, ...branch }) => ({
          branch_slug: branch.branch_slug || "",
          name: branch.name,
          region: branch.region,
          location: isCustomLocation ? customLocation : branch.location,
          district: branch.district || "",
          suburb: branch.suburb || "",
          landmark: branch.landmark || "",
          gpsAddress: branch.gpsAddress || "",
          phone_number: phoneNumber
        }))
      };

      const response = await axios.patch(
        `${Config.backendDomain}vendor/partner/${partner_slug}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("dealboxx_token")}`,
          },
        }
      );

      const { data } = response;
      if (data?.data?.status_code === "000" && !data?.data?.in_error) {
        toast.success(data?.data?.reason || "Partner updated successfully");
        navigate("/partners");
      } else {
        throw new Error(data?.data?.reason || "Failed to update partner");
      }
    } catch (error) {
      console.error("Error updating partner:", error);
      toast.error(error?.response?.data?.reason || error.message || "Failed to update partner");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Navbar page="Edit Partner" />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="p-6 py-8 mx-auto max-w-7xl"
        >
          {/* Back Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/partners")}
            className="flex gap-2 items-center mb-6 text-gray-600 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Partners</span>
          </motion.button>

          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 gap-6 lg:grid-cols-2"
          >
            {/* Left Side - Form */}
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="sticky top-6 p-6 bg-white rounded-3xl border border-gray-100 shadow-xl"
              >
                <div className="mb-6">
                  <label className="block mb-2 text-lg font-semibold text-gray-800">
                    Partner Name
                  </label>
                  <input
                    type="text"
                    value={partnerData.name}
                    onChange={(e) => {
                      setPartnerData((prev) => ({ ...prev, name: e.target.value }));
                      setErrors((prev) => ({ ...prev, partnerName: "" }));
                    }}
                    className={`w-full px-4 py-3 text-lg border-2 ${
                      errors.partnerName ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    } rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300`}
                    placeholder="Enter partner name"
                  />
                  <AnimatePresence>
                    {errors.partnerName && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex gap-1 items-center mt-1 text-sm text-red-500"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {errors.partnerName}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                <div className="space-y-4">
                  <div className="flex gap-2 items-center mb-4">
                    <div className="p-1.5 rounded-lg bg-[#FF6C6C]/10">
                      <MapPin className="w-5 h-5 text-[#FF6C6C]" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      {isEditing ? "Edit Branch" : "Add New Branch"}
                    </h2>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Branch Name
                      </label>
                      <input
                        type="text"
                        value={currentBranch.name}
                        onChange={(e) => {
                          setCurrentBranch((prev) => ({ ...prev, name: e.target.value }));
                          setErrors((prev) => ({ ...prev, branchName: "" }));
                        }}
                        className={`w-full px-4 py-2.5 text-base border-2 ${
                          errors.branchName ? 'border-red-300 bg-red-50' : 'border-gray-200'
                        } rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200`}
                        placeholder="Enter branch name"
                      />
                      <AnimatePresence>
                        {errors.branchName && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="flex gap-1 items-center mt-1 text-sm text-red-500"
                          >
                            <AlertCircle className="w-4 h-4" />
                            {errors.branchName}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    <div className="col-span-2">
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Phone Number
                        <span className="text-[#FF6C6C] ml-1">*</span>
                      </label>
                      <div className="relative">
                        <div className="flex absolute inset-y-0 left-0 items-center pl-4 pointer-events-none">
                          <span className="text-gray-500">233</span>
                        </div>
                        <input
                          type="text"
                          value={currentBranch.phoneNumber}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= 9) {
                              setCurrentBranch((prev) => ({
                                ...prev,
                                phoneNumber: value,
                              }));
                              setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                            }
                          }}
                          className={`w-full pl-14 pr-4 py-2.5 text-base border-2 ${
                            errors.phoneNumber ? 'border-red-300 bg-red-50' : 'border-gray-200'
                          } rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200`}
                          placeholder="Enter phone number"
                        />
                      </div>
                      <AnimatePresence>
                        {errors.phoneNumber ? (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="flex gap-1 items-center mt-1 text-sm text-red-500"
                          >
                            <AlertCircle className="w-4 h-4" />
                            {errors.phoneNumber}
                          </motion.p>
                        ) : (
                          <p className="mt-1 text-xs text-gray-500">
                            Enter 9 digits without the prefix
                          </p>
                        )}
                      </AnimatePresence>
                    </div>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Region
                      </label>
                      <div className="relative">
                        <select
                          value={currentBranch.region}
                          onChange={(e) => handleRegionChange(e.target.value)}
                          className={`w-full px-4 py-2.5 text-base border-2 ${
                            errors.region ? 'border-red-300 bg-red-50' : 'border-gray-200'
                          } rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200 appearance-none bg-white hover:border-gray-300 cursor-pointer`}
                        >
                          <option value="">Select Region</option>
                          {regionOptions.map((region) => (
                            <option key={region} value={region}>
                              {region}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none p-1.5 rounded-lg bg-[#FF6C6C]/10">
                          <ChevronDown className="w-4 h-4 text-[#FF6C6C]" />
                        </div>
                      </div>
                      <AnimatePresence>
                        {errors.region && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="flex gap-1 items-center mt-1 text-sm text-red-500"
                          >
                            <AlertCircle className="w-4 h-4" />
                            {errors.region}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {currentBranch.region && (
                      <div>
                        <label className="block mb-1.5 text-sm font-medium text-gray-700">
                          Location
                        </label>
                        <div className="relative">
                          <select
                            value={currentBranch.location}
                            onChange={(e) => handleLocationChange(e.target.value)}
                            className={`w-full px-4 py-2.5 text-base border-2 ${
                              errors.location ? 'border-red-300 bg-red-50' : 'border-gray-200'
                            } rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200 appearance-none bg-white hover:border-gray-300 cursor-pointer`}
                          >
                            <option value="">Select Location</option>
                            {availableLocations.map((location) => (
                              <option key={location} value={location}>
                                {location}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none p-1.5 rounded-lg bg-[#FF6C6C]/10">
                            <ChevronDown className="w-4 h-4 text-[#FF6C6C]" />
                          </div>
                        </div>
                        <AnimatePresence>
                          {errors.location && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="flex gap-1 items-center mt-1 text-sm text-red-500"
                            >
                              <AlertCircle className="w-4 h-4" />
                              {errors.location}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>

                  {isCustomLocation && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Custom Location
                      </label>
                      <input
                        type="text"
                        value={currentBranch.customLocation}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            customLocation: e.target.value,
                          }))
                        }
                        className={`w-full px-4 py-2.5 text-base border-2 ${
                          errors.customLocation ? 'border-red-300 bg-red-50' : 'border-gray-200'
                        } rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200`}
                        placeholder="Enter custom location"
                      />
                      <AnimatePresence>
                        {errors.customLocation && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="flex gap-1 items-center mt-1 text-sm text-red-500"
                          >
                            <AlertCircle className="w-4 h-4" />
                            {errors.customLocation}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}

                  {/* Optional Fields */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    <h3 className="col-span-2 text-sm font-medium text-gray-700">
                      Additional Details
                    </h3>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        District <OptionalLabel />
                      </label>
                      <input
                        type="text"
                        value={currentBranch.district}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            district: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200"
                        placeholder="Enter district name"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Suburb <OptionalLabel />
                      </label>
                      <input
                        type="text"
                        value={currentBranch.suburb}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            suburb: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200"
                        placeholder="Enter suburb name"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Landmark <OptionalLabel />
                      </label>
                      <input
                        type="text"
                        value={currentBranch.landmark}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            landmark: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200"
                        placeholder="Enter a nearby landmark"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        GPS Address <OptionalLabel />
                      </label>
                      <input
                        type="text"
                        value={currentBranch.gpsAddress}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            gpsAddress: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:ring-[#FF6C6C] focus:border-[#FF6C6C] transition-all duration-200"
                        placeholder="Enter GPS address"
                      />
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={applyBranch}
                    disabled={
                      !currentBranch.name ||
                      !currentBranch.region ||
                      (!currentBranch.location && !currentBranch.customLocation) ||
                      !currentBranch.phoneNumber
                    }
                    className="w-full py-3 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-xl flex items-center justify-center gap-2 hover:from-[#ff5555] hover:to-[#FF7F7F] transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg group mt-4"
                  >
                    <Check className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
                    {isEditing ? "Update Branch" : "Add Branch"}
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Side - Preview */}
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col h-full"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="flex-1 p-6 mb-6 bg-white rounded-3xl border border-gray-100 shadow-xl"
              >
                <div className="flex justify-between items-center mb-6">
                  <div className="flex gap-3 items-center">
                    <div className="p-2 rounded-xl bg-[#FF6C6C]/10">
                      <Building2 className="w-5 h-5 text-[#FF6C6C]" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      Branch Preview
                    </h2>
                  </div>
                  {partnerData.branches.length > 0 && (
                    <div className="px-3 py-1.5 bg-[#FF6C6C]/10 rounded-xl">
                      <span className="text-sm font-medium text-[#FF6C6C]">
                        {partnerData.branches.length}{" "}
                        {partnerData.branches.length === 1 ? "Branch" : "Branches"}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  {partnerData.branches.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <BranchPreview
                      branches={partnerData.branches}
                      onRemove={removeBranch}
                      onEdit={handleEditBranch}
                    />
                  )}
                </div>
              </motion.div>

              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={handleSubmit}
                disabled={
                  !partnerData.name ||
                  partnerData.branches.length === 0 ||
                  isSaving
                }
                className="w-full py-4 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white text-lg font-medium rounded-xl flex items-center justify-center gap-3 hover:from-[#ff5555] hover:to-[#FF7F7F] transition-all duration-200 shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-xl group"
              >
                {isSaving ? (
                  <div className="flex gap-2 items-center">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Saving Changes...</span>
                  </div>
                ) : (
                  <>
                    <Save className="w-6 h-6 transition-transform duration-200 group-hover:scale-110" />
                    <span>Save Changes</span>
                  </>
                )}
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Cancel Edit Button */}
      {isEditing && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            setCurrentBranch({
              branch_slug: "",
              name: "",
              region: "",
              location: "",
              customLocation: "",
              district: "",
              suburb: "",
              landmark: "",
              gpsAddress: "",
              phoneNumber: "",
            });
            setIsEditing(false);
            setIsCustomLocation(false);
            setErrors({});
          }}
          className="fixed right-6 top-24 p-2 text-gray-600 bg-white rounded-full border border-gray-200 shadow-lg transition-all duration-200 hover:shadow-xl hover:text-gray-900"
        >
          <X className="w-5 h-5" />
        </motion.button>
      )}
    </>
  );
};

export default PartnerEdit;
